// src/components/Header.tsx
import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import { getUserRole } from "../utils/authUtils";
import "../styles/header.css";
import { AuthContext } from '../App';

interface HeaderProps {
  isAuthenticated: boolean;
  onLoginClick: () => void;
  onHomeClick: () => void;
  onLogoutClick: () => void;
}

type SubmenuName = "societes" | "opportunites" | "devis" | "factures" | "admin";

const Header: React.FC<HeaderProps> = ({
  isAuthenticated,
  onLoginClick,
  onHomeClick,
  onLogoutClick,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<null | SubmenuName>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [roleLoading, setRoleLoading] = useState(true);

  const { handleLogout } = useContext(AuthContext);

  const toggleMenu = () => setIsMenuOpen((v) => !v);
  const toggleSubmenu = (name: SubmenuName) =>
    setActiveSubmenu((v) => (v === name ? null : name));
  const closeAll = () => {
    setIsMenuOpen(false);
    setActiveSubmenu(null);
  };


  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && user.email) {
        const email = user.email.trim().toLowerCase();
        const role = await getUserRole(email);
        setUserRole(role || null);
      } else {
        setUserRole(null);
      }
      setRoleLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return (
    <header className="header">
      <div className="logo">
        <Link to="/" onClick={() => { onHomeClick(); closeAll(); }}>
          <img src="/logo-ne.png" alt="Logo NormExport" className="home-image" />
        </Link>
      </div>

      {isAuthenticated && (
        <button className="hamburger" onClick={toggleMenu} aria-label="Ouvrir le menu">
          &#9776;
        </button>
      )}

      <nav>
        {isAuthenticated && !roleLoading ? (
          <ul className={`nav-menu ${isMenuOpen ? "open" : ""}`}>
            <li>
              <Link to="/" onClick={() => { onHomeClick(); closeAll(); }}>
                <span className="icon icon-outline"><i className="bi bi-house-door" /></span>
                <span className="icon icon-fill"><i className="bi bi-house-door-fill" /></span>
                Accueil
              </Link>
            </li>

            {/* Sociétés */}
            {(userRole === "admin" || userRole === "demo") && (
              <li className={`has-submenu ${activeSubmenu === "societes" ? "open" : ""}`}>
                <div className="submenu-toggle" onClick={() => isMenuOpen && toggleSubmenu("societes")}>
                  <span className="icon icon-outline"><i className="bi bi-building" /></span>
                  <span className="icon icon-fill"><i className="bi bi-building-fill" /></span>
                  Sociétés <i className="bi bi-chevron-down caret" />
                </div>
                <ul className="submenu">
                  <li><Link to="/societes" onClick={closeAll}>Mes Sociétés</Link></li>
                  {userRole === "admin" && <li><Link to="/societes-foodinnov" onClick={closeAll}>Sociétés Foodinnov</Link></li>}
                </ul>
              </li>
            )}

            {/* Opportunités */}
            <li className={`has-submenu ${activeSubmenu === "opportunites" ? "open" : ""}`}>
              <div className="submenu-toggle" onClick={() => isMenuOpen && toggleSubmenu("opportunites")}>
                <span className="icon icon-outline"><i className="bi bi-briefcase" /></span>
                <span className="icon icon-fill"><i className="bi bi-briefcase-fill" /></span>
                Demandes <i className="bi bi-chevron-down caret" />
              </div>
              <ul className="submenu">
                {(userRole === "admin" || userRole === "demo") && (
                  <li><Link to="/opportunites" onClick={closeAll}>Mes Demandes</Link></li>
                )}
                {(userRole === "admin" || userRole === "foodinnov") && (
                  <li><Link to="/opportunites-foodinnov" onClick={closeAll}>Demandes Foodinnov</Link></li>
                )}
              </ul>
            </li>

            {/* Devis */}
            <li className={`has-submenu ${activeSubmenu === "devis" ? "open" : ""}`}>
              <div className="submenu-toggle" onClick={() => isMenuOpen && toggleSubmenu("devis")}>
                <span className="icon icon-outline"><i className="bi bi-file-earmark-text" /></span>
                <span className="icon icon-fill"><i className="bi bi-file-earmark-text-fill" /></span>
                Devis <i className="bi bi-chevron-down caret" />
              </div>
              <ul className="submenu">
                {(userRole === "admin" || userRole === "demo") && (
                  <li><Link to="/devis" onClick={closeAll}>Mes Devis</Link></li>
                )}
                {(userRole === "admin" || userRole === "foodinnov") && (
                  <li><Link to="/devis-foodinnov" onClick={closeAll}>Devis Foodinnov</Link></li>
                )}
              </ul>
            </li>

            {/* Factures */}
            <li className={`has-submenu ${activeSubmenu === "factures" ? "open" : ""}`}>
              <div className="submenu-toggle" onClick={() => isMenuOpen && toggleSubmenu("factures")}>
                <span className="icon icon-outline"><i className="bi bi-receipt" /></span>
                <span className="icon icon-fill"><i className="bi bi-receipt-cutoff" /></span>
                Factures <i className="bi bi-chevron-down caret" />
              </div>
              <ul className="submenu">
                {(userRole === "admin" || userRole === "demo") && (
                  <li><Link to="/factures" onClick={closeAll}>Mes Factures</Link></li>
                )}
                {userRole === "admin" && (
                  <li><Link to="/factures-zak" onClick={closeAll}>Factures Zak</Link></li>
                )}
                {(userRole === "admin" || userRole === "foodinnov") && (
                  <li><Link to="/factures-foodinnov" onClick={closeAll}>Factures Foodinnov</Link></li>
                )}
              </ul>
            </li>

            {/* Planner (admin uniquement) */}
            {userRole === "admin" && (
              <li>
                <Link to="/taches-administration" onClick={closeAll}>
                  <span className="icon icon-outline"><i className="bi bi-calendar-check" /></span>
                  <span className="icon icon-fill"><i className="bi bi-calendar-check-fill" /></span>
                  Planner
                </Link>
              </li>
            )}

            {/* Mails Types (admin uniquement) */}
            {userRole === "admin" && (
              <li>
                <Link to="/mails-types" onClick={closeAll}>
                  <span className="icon icon-outline"><i className="bi bi-envelope" /></span>
                  <span className="icon icon-fill"><i className="bi bi-envelope-fill" /></span>
                  Mails Types
                </Link>
              </li>
            )}

            {/* Administration */}
            {userRole === "admin" && (
              <li className={`has-submenu ${activeSubmenu === "admin" ? "open" : ""}`}>
                <div className="submenu-toggle" onClick={() => isMenuOpen && toggleSubmenu("admin")}>
                  <span className="icon icon-outline"><i className="bi bi-gear" /></span>
                  <span className="icon icon-fill"><i className="bi bi-gear-fill" /></span>
                  Administration <i className="bi bi-chevron-down caret" />
                </div>
                <ul className="submenu">
                  <li><Link to="/admin" onClick={closeAll}>Gestion des utilisateurs</Link></li>
                  <li><Link to="/database" onClick={closeAll}>Sauvegarde / Restaure</Link></li>
                  <li><Link to="/logs" onClick={closeAll}>Voir les logs</Link></li>
                  <li><Link to="/admin/pdf-rep-tool" onClick={closeAll}>Outil de repérage</Link></li>
                  <li><Link to="/admin/pdf-tool-devis" onClick={closeAll}>Outil PDF Devis</Link></li>
                </ul>
              </li>
            )}


            {/* Mon Profil */}
            <li>
              <Link to="/profil" onClick={closeAll}>
                <span className="icon icon-outline"><i className="bi bi-person" /></span>
                <span className="icon icon-fill"><i className="bi bi-person-fill" /></span>
                Mon profil
              </Link>
            </li>

            {/* Déconnexion */}
            <li className="logout" onClick={() => { onLogoutClick(); closeAll(); }}>
              Déconnexion
            </li>
          </ul>
        ) : isAuthenticated && roleLoading ? (
          <div className="nav-loading">Chargement du menu...</div>
        ) : null}
      </nav>
    </header>
  );
};

export default Header;
